import {Mental} from "../Mental";
import {MentalNormalSymbol} from "../reel/symbols/MentalNormalSymbol";
import {MentalSymbolStates} from "../reel/MentalSymbol";
import {MentalSymbolMultiplier} from "../reel/symbols/multiplier/MentalSymbolMultiplier";

export class FeaturePresentationHelper {
    public static _possibleSymbolSizes: number[] = [198, 148, 132, 118, 99, 84, 74, 66, 60, 56, 50, 45, 40, 32, 26];
    public static reelHeight: number[] = [594, 396, 594, 396, 594];

    public static possibleSymbolOnReels: number[] = [3, 2, 3, 2, 3];
    public static _alreadyResized: boolean[] = [false, false, false, false, false];
    public static _symbolHeightOnReel: number[] = [-1, -1, -1, -1, -1];

    public static resizeSymbolWithInReel(reelId: number, isEnhancer: boolean, isWildRevealing: boolean = false): void {
        let symbols: MentalNormalSymbol[] = Mental.reelArea.getReel(reelId).getAllVisibleSymbols() as MentalNormalSymbol[];

        if (isEnhancer) {
            symbols = Mental.reelArea.getReel(reelId).getAllVisibleStopSymbolsWithEnhancerSymbols() as MentalNormalSymbol[];
        }

        const symWithAllSplits: MentalNormalSymbol[] = FeaturePresentationHelper.getAllSymbolOnReelWithSplits(symbols);

        const symbolHeight: number = FeaturePresentationHelper.getSymbolHeights(
            symWithAllSplits.length,
            (reelId === 1 || reelId === 3),
            isEnhancer);

        if (FeaturePresentationHelper.possibleSymbolOnReels[reelId] * symbolHeight === FeaturePresentationHelper.reelHeight[reelId]) {
            return;   // if no split present on reel
        }

        if (!this._alreadyResized[reelId]) {
            this._alreadyResized[reelId] = true;
        }
        this._symbolHeightOnReel[reelId] = symbolHeight;
        FeaturePresentationHelper.changeSymbolStateBasedOnHeight(symWithAllSplits, symbolHeight, isWildRevealing, reelId);
    }

    public static getAllSymbolOnReelWithSplits(symbols: MentalNormalSymbol[]): MentalNormalSymbol[] {
        const reelSymbols: MentalNormalSymbol[] = [];
        for (let i: number = 0; i < symbols.length; i++) {
            if (symbols[i]._splitSymbols.length) {
                for (let j = 0; j < symbols[i]._splitSymbols.length; j++) {
                    reelSymbols.push(symbols[i]._splitSymbols[j]);
                }
            } else {
                reelSymbols.push(symbols[i]);
            }
        }
        return reelSymbols;
    }

    public static repositionMultiplierOnResize(sym: MentalNormalSymbol): void {
        if (sym._multiplierAlreadyResized) {
            return;
        }
        let symbolMultiplier: MentalSymbolMultiplier = sym._topLayer.children[0] as MentalSymbolMultiplier;
        sym._topLayer.position.y = sym._symbolLayer.position.y;
        symbolMultiplier.position.y = (sym.currentSymbolHeight - symbolMultiplier.height);
    }

    private static getSymbolHeights(totalSymbolOnReel: number, isSpecialSymbolReel: boolean, isEnhancer: boolean): number {
        const availableHeight: number = isSpecialSymbolReel ? isEnhancer ? 198 * 2.5 : 198 * 2 : 198 * 3;
        const eachSymbolHeight: number = availableHeight / totalSymbolOnReel;
        return eachSymbolHeight;
    }

    private static changeSymbolStateBasedOnHeight(symWithAllSplits: MentalNormalSymbol[], symbolHeight: number, isWildRevealing: boolean, reelId: number): void {
        const actualHeight: number = this._possibleSymbolSizes.reduce((prev: number, curr: number) => {
            return (Math.abs(curr - symbolHeight) < Math.abs(prev - symbolHeight) ? curr : prev);
        });

        let canSkipWildAnim: boolean,
            symbol: MentalNormalSymbol;

        for (let i: number = 0; i < symWithAllSplits.length; i++) {
            symbol = symWithAllSplits[i];
            if (isWildRevealing) {
                canSkipWildAnim = false;
            } else {
                canSkipWildAnim = (symbol.symbolName === "W");
            }
            switch (actualHeight) {
                case 198:
                    symbol.setState(MentalSymbolStates.NONE, 198);
                    break;
                case 148:
                    symbol.setState(MentalSymbolStates.ONE_FORTY_EIGHT, symbolHeight, canSkipWildAnim);
                    break;
                case 132:
                    symbol.setState(MentalSymbolStates.ONE_THIRTY_TWO, symbolHeight, canSkipWildAnim);
                    break;
                case 118:
                    symbol.setState(MentalSymbolStates.ONE_HUNDRED_EIGHTEEN, symbolHeight, canSkipWildAnim);
                    break;
                case 99:
                    symbol.setState(MentalSymbolStates.NINETY_NINE, symbolHeight, canSkipWildAnim);
                    break;
                case 84:
                    symbol.setState(MentalSymbolStates.EIGHTY_FOUR, symbolHeight, canSkipWildAnim);
                    break;
                case 74:
                    symbol.setState(MentalSymbolStates.SEVENTY_FOUR, symbolHeight, canSkipWildAnim);
                    break;
                case 66:
                    symbol.setState(MentalSymbolStates.SIXTY_SIX, symbolHeight, canSkipWildAnim);
                    break;
                case 60:
                    symbol.setState(MentalSymbolStates.SIXTY, symbolHeight, canSkipWildAnim);
                    break;
                case 56:
                    symbol.setState(MentalSymbolStates.FIFTY_SIX, symbolHeight, canSkipWildAnim);
                    break;
                case 50:
                    symbol.setState(MentalSymbolStates.FIFTY, symbolHeight, canSkipWildAnim);
                    break;
                case 45:
                    symbol.setState(MentalSymbolStates.FORTY_FIVE, symbolHeight, canSkipWildAnim);
                    break;
                case 40:
                    symbol.setState(MentalSymbolStates.FORTY, symbolHeight, canSkipWildAnim);
                    break;
                case 32:
                    symbol.setState(MentalSymbolStates.THIRTY_TWO, symbolHeight, canSkipWildAnim);
                    break;
                case 26:
                    symbol.setState(MentalSymbolStates.TWENTY_SIX, symbolHeight, canSkipWildAnim);
                    break;
            }

            const symSpine: PIXI.spine.Spine = symbol._symbolLayer.children[0] as PIXI.spine.Spine;

            if (actualHeight !== symbolHeight) {
                symSpine.scale.y = symbolHeight / actualHeight;
            }

            symbol._yOffset = 0;

            // resetting it again, since it is a spine, which needs its transforms updated.
            symSpine.position.y = (symbolHeight / 2);

            // actual calculation for y
            symSpine.position.y += (i * symbolHeight) - symbol._symbolLayer.position.y;
            if (symbol._symbolLayer.parent && symbol._symbolLayer.parent.name === "root") {
                symSpine.position.y -= (symbol._symbolLayer.parent.position.y);
            }

            // check to verify there is multiplier on symbol.
            if (symbol._topLayer.children.length && symbol._symbolMultiplierValue > 1) {
                this.repositionMultiplierOnXWaysResize(symbol, i);
            }

            // #299
            if (symbol.symbolName === "DG") {
                const innerSymId: number = symbol._symbolLayer.parent.getChildIndex(symbol._symbolLayer);
                const symId: number = Mental.model.gameData.deadSymPos[reelId];
                Mental.deadFeaturePresentation.adjustActiveAnimOnResize(reelId, symId, innerSymId, symbolHeight, i);
            }
        }
    }

    private static repositionMultiplierOnXWaysResize(symbol: MentalNormalSymbol, symIndexOnReel: number) {
        let symbolMultiplier: MentalSymbolMultiplier = symbol._topLayer.children[0] as MentalSymbolMultiplier;

        symbolMultiplier.position.y = 0;

        symbolMultiplier.position.y += (symbol.currentSymbolHeight * (symIndexOnReel + 1)) - symbol._symbolLayer.position.y;

        if (symbol._symbolLayer.parent && symbol._symbolLayer.parent.name === "root") {
            symbolMultiplier.position.y -= (symbol._symbolLayer.parent.position.y);
        }

        symbolMultiplier.position.y -= symbolMultiplier.height;

        symbol._multiplierAlreadyResized = true;
    }
}